/**
 * Weather and time-of-day state. Pure data + pure interpolation: the simulation owns it
 * (it drives detection ranges, aim wander and vessel speeds) and the renderer reads it.
 */

import { clamp, clamp01, lerp, TAU } from './math';

export interface WeatherState {
  /** Douglas sea state, 3 (slight) .. 7 (high). Drives wave amplitude and steepness. */
  seaState: number;
  /** Wind heading in radians — the direction the wind blows toward. */
  windDir: number;
  /** Wind speed, m/s. Drives foam streaking and spray. */
  windSpeed: number;
  /** 0 = crystal clear, 1 = dense fog. Scales visual and detection range. */
  fogDensity: number;
  /** 0..1 rainfall intensity. */
  rain: number;
  /** 0..1 likelihood/frequency of lightning strikes. */
  lightning: number;
  /** Hours, 0..24. 12 = noon. */
  timeOfDay: number;
  /** 0..1 cloud cover. 1 = full overcast (the reference condition). */
  cloudCover: number;
}

export type WeatherPresetName =
  | 'clearDay'
  | 'overcastDay'
  | 'fogBank'
  | 'thickFog'
  | 'dusk'
  | 'night'
  | 'nightStorm'
  | 'storm'
  | 'severeStorm'
  | 'dawnFog';

export const WEATHER_PRESETS: Record<WeatherPresetName, WeatherState> = {
  clearDay: { seaState: 3.5, windDir: 0.6, windSpeed: 6, fogDensity: 0.12, rain: 0, lightning: 0, timeOfDay: 11, cloudCover: 0.25 },
  // The visual contract condition: heavy North Atlantic overcast, building sea.
  overcastDay: { seaState: 4.8, windDir: 0.55, windSpeed: 16, fogDensity: 0.34, rain: 0, lightning: 0, timeOfDay: 10.5, cloudCover: 1 },
  fogBank: { seaState: 4.5, windDir: 0.4, windSpeed: 7, fogDensity: 0.78, rain: 0, lightning: 0, timeOfDay: 9, cloudCover: 0.85 },
  /*
   * Shift 4's mid-shift close-in, so the weather can be an event rather than a backdrop.
   *
   * 0.85 rather than something more dramatic, and the ceiling is set by the gunboat's own
   * doctrine: she stands off at 600 m to strafe, and `visibilityFactor` puts her
   * silhouette at 807 m here against 1029 m in a plain fog bank. She is still something
   * the player can see while she is shooting at them, with margin. At 0.92 that number
   * falls to 584 m — inside her firing range — and the shift stops being tense and starts
   * being a player emptying the magazine at radar markers.
   */
  thickFog: { seaState: 4.6, windDir: 0.4, windSpeed: 9, fogDensity: 0.85, rain: 0, lightning: 0, timeOfDay: 9.3, cloudCover: 0.95 },
  dusk: { seaState: 4.5, windDir: 0.8, windSpeed: 10, fogDensity: 0.28, rain: 0, lightning: 0, timeOfDay: 19.5, cloudCover: 0.7 },
  night: { seaState: 4.5, windDir: 0.9, windSpeed: 11, fogDensity: 0.3, rain: 0, lightning: 0, timeOfDay: 23, cloudCover: 0.75 },
  nightStorm: { seaState: 6.3, windDir: 1.3, windSpeed: 24, fogDensity: 0.45, rain: 0.8, lightning: 0.8, timeOfDay: 23.5, cloudCover: 1 },
  storm: { seaState: 6.0, windDir: 1.1, windSpeed: 21, fogDensity: 0.42, rain: 0.6, lightning: 0.35, timeOfDay: 14, cloudCover: 1 },
  severeStorm: { seaState: 7.0, windDir: 1.25, windSpeed: 28, fogDensity: 0.52, rain: 0.95, lightning: 0.9, timeOfDay: 16, cloudCover: 1 },
  dawnFog: { seaState: 4.8, windDir: 0.3, windSpeed: 8, fogDensity: 0.7, rain: 0, lightning: 0, timeOfDay: 5.8, cloudCover: 0.8 },
};

export function cloneWeather(w: WeatherState): WeatherState {
  return { ...w };
}

/** Shortest-path interpolation for the wind heading, linear for everything else. */
export function lerpWeather(a: WeatherState, b: WeatherState, t: number): WeatherState {
  const k = clamp01(t);
  let dDir = (b.windDir - a.windDir) % TAU;
  if (dDir > Math.PI) dDir -= TAU;
  if (dDir < -Math.PI) dDir += TAU;

  // Time of day must also take the short way around midnight.
  let dTime = (b.timeOfDay - a.timeOfDay) % 24;
  if (dTime > 12) dTime -= 24;
  if (dTime < -12) dTime += 24;

  return {
    seaState: lerp(a.seaState, b.seaState, k),
    windDir: a.windDir + dDir * k,
    windSpeed: lerp(a.windSpeed, b.windSpeed, k),
    fogDensity: lerp(a.fogDensity, b.fogDensity, k),
    rain: lerp(a.rain, b.rain, k),
    lightning: lerp(a.lightning, b.lightning, k),
    timeOfDay: (a.timeOfDay + dTime * k + 24) % 24,
    cloudCover: lerp(a.cloudCover, b.cloudCover, k),
  };
}

/** 0 at solar midnight, 1 at solar noon. Used for lighting and detection ranges. */
export function daylightFactor(timeOfDay: number): number {
  // Sunrise ~6, sunset ~20 for a temperate North Atlantic summer.
  const t = ((timeOfDay % 24) + 24) % 24;
  const angle = ((t - 6) / 14) * Math.PI; // 6h -> 0, 20h -> PI
  if (t < 6 || t > 20) {
    // Night: a small floor so the world is never pitch black.
    const nightT = t < 6 ? (t + 4) / 10 : (t - 20) / 10;
    return lerp(0.0, 0.06, 1 - Math.abs(nightT * 2 - 1));
  }
  return clamp01(Math.sin(angle)) * 0.94 + 0.06;
}

export function isNight(timeOfDay: number): boolean {
  const t = ((timeOfDay % 24) + 24) % 24;
  return t < 6.2 || t > 19.8;
}

/**
 * Sun (or moon) direction for the given time of day.
 * A simple arc: rises in the east (+X), sets in the west (-X).
 */
export function sunDirection(timeOfDay: number): { x: number; y: number; z: number } {
  const t = ((timeOfDay % 24) + 24) % 24;
  // Map 6..20 to 0..PI for the solar arc; outside that the moon follows the same path.
  const night = isNight(t);
  const phase = night ? (((t + 4) % 24) / 10) * Math.PI : ((t - 6) / 14) * Math.PI;
  const elevation = Math.sin(clamp(phase, 0, Math.PI));
  return {
    x: Math.cos(phase),
    // Keep the light just above the horizon at minimum so specular never inverts.
    y: Math.max(0.08, elevation * 0.85),
    z: -0.35,
  };
}

/**
 * Visibility multiplier from fog, applied to detection and identification ranges.
 * 1 = full range, small values = heavily reduced.
 */
export function visibilityFactor(w: WeatherState): number {
  const fog = 1 - Math.pow(clamp01(w.fogDensity), 0.8) * 0.92;
  const dark = isNight(w.timeOfDay) ? 0.5 : 1;
  const rain = 1 - clamp01(w.rain) * 0.25;
  return clamp(fog * dark * rain, 0.05, 1);
}

/** Extra aim wander (radians) induced by sea state and wind. */
export function aimDisturbance(w: WeatherState): number {
  const sea = clamp01((w.seaState - 3) / 4);
  const wind = clamp01(w.windSpeed / 30);
  return 0.0006 + sea * 0.0028 + wind * 0.0012;
}
